import type { Backlink } from '@api/types/backlinks';
import type {
  AttachDocumentDto,
  CreateMeetingDto,
  DetachDocumentDto,
  MeetingComment,
  MeetingItem,
  UpdateMeetingDto,
} from '@/api';
import { GetMeetingsParams, MeetingResponse, MeetingsResponse } from '@/api';
import { ENDPOINTS } from '@/api/endpoints';
import API from '@api/API';
import { createFormData } from '@utils/formDataBuilder';
import { AxiosRequestConfig } from 'axios';

/**
 * Meetings API service
 * Provides methods for interacting with meeting endpoints
 */
export const meetingsApi = {
  getMeetings: async (auditId: string, params?: GetMeetingsParams): Promise<MeetingsResponse> => {
    const res = await API().get(ENDPOINTS.meetings.list(auditId), { params });
    return res.data;
  },

  getAllMeetings: async (auditId: string): Promise<{ meetings: MeetingItem[] }> => {
    const res = await API().get(ENDPOINTS.meetings.all(auditId));
    return res.data;
  },

  getMeeting: async (auditId: string, meetingId: string | number): Promise<MeetingResponse> => {
    const res = await API().get(ENDPOINTS.meetings.one(auditId, meetingId));
    return res.data;
  },

  createMeeting: async (
    auditId: string,
    data: CreateMeetingDto,
  ): Promise<{ meeting: MeetingItem }> => {
    const formData = createFormData(data);
    const res = await API().post(ENDPOINTS.meetings.create(auditId), formData);
    return res.data;
  },

  updateMeeting: async (
    auditId: string,
    meetingId: string | number,
    data: UpdateMeetingDto,
    config?: AxiosRequestConfig,
  ): Promise<{ meeting: MeetingItem }> => {
    const res = await API().postForm(ENDPOINTS.meetings.update(auditId, meetingId), data, config);
    return res.data;
  },

  /**
   * Refused 400 `{ message }` while the meeting is cited — the sentence counts the
   * places, never names one (the route is client-reachable) — and 503 when the
   * reference check itself could not run. Both are the caller's to show verbatim.
   */
  deleteMeeting: async (auditId: string, meetingId: string | number): Promise<string> => {
    const res = await API().delete(ENDPOINTS.meetings.delete(auditId, meetingId));
    return res.data;
  },

  getBacklinks: async (
    auditId: string,
    meetingId: string | number,
  ): Promise<{ backlinks: Backlink[] }> => {
    const res = await API().get(ENDPOINTS.meetings.backlinks(auditId, meetingId));
    return res.data;
  },

  attachDocument: async (
    auditId: string,
    meetingId: string | number,
    data: AttachDocumentDto,
  ): Promise<{ meeting: MeetingItem }> => {
    const res = await API().post(ENDPOINTS.meetings.attachDocument(auditId, meetingId), data);
    return res.data;
  },

  detachDocument: async (
    auditId: string,
    meetingId: string | number,
    data: DetachDocumentDto,
  ): Promise<{ meeting: MeetingItem }> => {
    const res = await API().post(ENDPOINTS.meetings.detachDocument(auditId, meetingId), data);
    return res.data;
  },

  addComment: async (
    auditId: string,
    meetingId: string | number,
    data: { text: string },
  ): Promise<{ comment: MeetingComment }> => {
    const res = await API().post(ENDPOINTS.meetings.comment(auditId, meetingId), data);
    return res.data;
  },
};
